import React from "react";
import {
  Box,
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "react-toastify";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import CustomTextField from "../CustomTextField";
import CustomSelect from "../CustomSelect";
import CustomLabel from "../CustomLabel";
import { COLORS } from "../../assets/theme";
import {
  createClassCategory,
  getAllClassCategories,
  updateClassCategory,
} from "../../services/ClassesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { ClassCategory } from "./types";
import { ACTIVE_OPTIONS, fromActiveLabel, toActiveLabel } from "./classUtils";

interface ClassCategoriesDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called after any create/update so callers can refresh their category list. */
  onChanged?: () => void;
}

const emptyForm = { name: "", description: "", active: "Active" };

/**
 * Categories only exist so a package can scope itself to one group of classes,
 * so they live in a small manager dialog instead of their own page.
 */
const ClassCategoriesDialog: React.FC<ClassCategoriesDialogProps> = ({
  open,
  onClose,
  onChanged,
}) => {
  const [categories, setCategories] = React.useState<ClassCategory[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [formOpen, setFormOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [form, setForm] = React.useState(emptyForm);

  const fetchCategories = React.useCallback(async () => {
    setLoading(true);
    try {
      setCategories(await getAllClassCategories());
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to load categories."));
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!open) return;
    setFormOpen(false);
    setEditingId(null);
    setForm(emptyForm);
    fetchCategories();
  }, [open, fetchCategories]);

  const startEdit = (category: ClassCategory) => {
    setEditingId(String(category.id));
    setForm({
      name: String(category.name || ""),
      description: String(category.description || ""),
      active: toActiveLabel(category.active),
    });
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Category name is required.");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description ? form.description : undefined,
        active: fromActiveLabel(form.active),
      };

      if (editingId) {
        await updateClassCategory(editingId, payload);
        toast.success("Category updated successfully!");
      } else {
        await createClassCategory(payload);
        toast.success("Category created successfully!");
      }

      setFormOpen(false);
      setEditingId(null);
      setForm(emptyForm);
      await fetchCategories();
      onChanged?.();
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to save the category."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      paperSx={{ minWidth: { sm: 640 }, maxWidth: 800 }}
      title="Class Categories"
      actions={<CustomButton label="Close" variant="outlined" onClick={onClose} />}
    >
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <CustomButton
          label={formOpen ? "Cancel" : "Add Category"}
          startIcon={formOpen ? undefined : <AddIcon />}
          variant={formOpen ? "outlined" : "contained"}
          bgColor={formOpen ? undefined : COLORS.MainColor}
          textColor={formOpen ? undefined : COLORS.WhiteText}
          onClick={() => {
            if (formOpen) {
              setFormOpen(false);
              setEditingId(null);
              setForm(emptyForm);
            } else {
              setEditingId(null);
              setForm(emptyForm);
              setFormOpen(true);
            }
          }}
        />
      </Box>

      {formOpen && (
        <Box
          sx={{
            border: "1px solid #eee",
            borderRadius: 2,
            p: 2,
            mb: 2,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 2,
          }}
        >
          <Box>
            <CustomLabel
              text="Name"
              fontSize={14}
              fontWeight={500}
              highlightLabelStar
            />
            <CustomTextField
              value={form.name}
              onChange={(e: any) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              fullWidth
            />
          </Box>
          <Box>
            <CustomLabel text="Status" fontSize={14} fontWeight={500} />
            <CustomSelect
              value={form.active}
              options={ACTIVE_OPTIONS}
              onChange={(v: any) =>
                setForm((prev) => ({ ...prev, active: String(v) }))
              }
              fullWidth
            />
          </Box>
          <Box sx={{ gridColumn: "1 / -1" }}>
            <CustomLabel text="Description" fontSize={14} fontWeight={500} />
            <CustomTextField
              value={form.description}
              onChange={(e: any) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
              fullWidth
            />
          </Box>
          <Box sx={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end" }}>
            <CustomButton
              label={editingId ? "Save Category" : "Create Category"}
              variant="contained"
              bgColor={COLORS.MainColor}
              textColor={COLORS.WhiteText}
              loading={saving}
              disabled={saving}
              onClick={handleSave}
            />
          </Box>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : categories.length === 0 ? (
        <Typography sx={{ color: "#777", fontSize: 14 }}>
          No categories yet. Categories are optional — they let a package cover one
          group of classes instead of all of them.
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell>{category.name || "-"}</TableCell>
                  <TableCell>{category.description || "-"}</TableCell>
                  <TableCell>{toActiveLabel(category.active)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => startEdit(category)}>
                        <EditIcon sx={{ color: COLORS.MainColor, width: 20 }} />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </CustomDialog>
  );
};

export default ClassCategoriesDialog;
